import {
  buildGa4ChildBasketLineItem,
  buildGa4PrimaryCoverBasketLineItem,
  resolveGa4ProductImageUrl,
} from '@amblique/king-living/app/analytics/ga4/basket-ga4';
import {roundGa4Currency} from '@amblique/king-living/app/analytics/ga4/currency';
import {resolveKingGuardGa4Item} from '@amblique/king-living/app/analytics/ga4/king-guard';
import {trackAddToCart} from '@amblique/king-living/app/analytics/ga4/events';
import {
  CHECKOUT_GET_PRODUCTS_PARAMS,
  chunk,
  GET_PRODUCTS_PAGE_SIZE,
  GET_PRODUCTS_PARAMS,
} from '@amblique/king-living/app/utils/api-utils';
import {parseSelectedOptionsUIPayload} from '@amblique/king-living/app/utils/cart-utils';
import {getSelectedVariantProduct} from '@amblique/king-living/app/utils/customize-selections';
import {getProductEffectivePrice} from '@amblique/king-living/app/utils/product-utils';

const buildGa4OptionProductMetaMaps = (products, {includePrices = true} = {}) => {
  const imageMap = {};
  const nameMap = {};
  const priceMap = {};
  const productsById = {};

  (products ?? []).forEach((product) => {
    const id = product?.id ?? product?.productId;

    if (!id) return;

    productsById[id] = product;

    if (includePrices) {
      const effectivePrice = getProductEffectivePrice(product);

      if (effectivePrice != null) {
        priceMap[id] = effectivePrice;
      }
    }

    const imgUrl = resolveGa4ProductImageUrl(product);

    if (imgUrl) {
      imageMap[id] = imgUrl;
    }

    if (product.name) {
      nameMap[id] = product.name;
    }
  });

  return {imageMap, nameMap, priceMap, productsById};
};

/** PDP `setOptionPriceMap` is authoritative when present; otherwise fall back to cart line / stub. */
const resolveGa4ItemPrice = (productId, {priceMap = null, product = null, linePrice} = {}) => {
  const mapped = productId != null ? priceMap?.[productId] : null;

  if (mapped != null && Number.isFinite(Number(mapped))) {
    return roundGa4Currency(Number(mapped)) ?? 0;
  }

  const fallback = [
    product && getProductEffectivePrice(product),
    product?.salePrice > 0 ? product.salePrice : null,
    linePrice
  ]
    .map(Number)
    .find((price) => Number.isFinite(price) && price >= 0);

  return roundGa4Currency(fallback ?? 0) ?? 0;
};

const resolveGa4CustomizeEventValue = (displayTotal, configuredTotal, computedTotal) => {
  const picked = [displayTotal, configuredTotal, computedTotal]
    .map(Number)
    .find((value) => Number.isFinite(value) && value > 0);

  return roundGa4Currency(picked ?? computedTotal) ?? computedTotal;
};

/**
 * Resolve product meta for GA4 basket/option events.
 *
 * @param {Object} args
 * @param {Object} [args.productsById] - Preloaded products keyed by id (e.g. checkout
 *   inventory map). When every requested id is present, **no** `getProducts` call is made.
 * @param {boolean} [args.useBasketProductParams=false] - When fetching missing ids, use
 *   lean checkout expand instead of full {@link GET_PRODUCTS_PARAMS} (ATC/customize).
 */
export const fetchGa4OptionProductMeta = async ({
  api,
  getToken,
  productIds = [],
  inventoryIds = null,
  includePrices = true,
  productsById: preloadedProductsById = null,
  useBasketProductParams = false,
}) => {
  const ids = [...new Set(productIds.filter(Boolean))];
  const preloaded = preloadedProductsById && typeof preloadedProductsById === 'object' ?
    preloadedProductsById :
    {};
  const seeded = buildGa4OptionProductMetaMaps(
    ids.map((id) => preloaded[id]).filter(Boolean),
    {includePrices}
  );

  if (!ids.length) {
    return seeded;
  }

  const missingIds = ids.filter((id) => !seeded.productsById[id]);

  if (!missingIds.length) {
    return seeded;
  }

  if (!api?.shopperProducts) {
    return seeded;
  }

  const token = await getToken();
  const batches = chunk(missingIds, GET_PRODUCTS_PAGE_SIZE);
  const fetchParams = useBasketProductParams ? CHECKOUT_GET_PRODUCTS_PARAMS : GET_PRODUCTS_PARAMS;
  const results = await Promise.all(
    batches.map((batchIds) => api.shopperProducts
      .getProducts({
        parameters: {
          ids: batchIds.join(','),
          ...fetchParams,
          ...(inventoryIds ? {inventoryIds} : {}),
        },
        headers: {Authorization: `Bearer ${token}`},
      })
      .then((res) => res?.data ?? []))
  );
  const fetched = buildGa4OptionProductMetaMaps(results.flat(), {includePrices});

  return {
    imageMap: {...seeded.imageMap, ...fetched.imageMap},
    nameMap: {...seeded.nameMap, ...fetched.nameMap},
    priceMap: {...seeded.priceMap, ...fetched.priceMap},
    productsById: {...seeded.productsById, ...fetched.productsById},
  };
};

const buildGa4AddToCartItemsFromCustomize = ({
  masterProduct,
  primaryItem,
  childItems = [],
  currency,
  optionPriceMap = null,
  optionImageMap = null,
  optionNameMap = null,
  productsById = {},
}) => {
  if (!primaryItem?.productId || !masterProduct) {
    return [];
  }

  const payload = parseSelectedOptionsUIPayload(primaryItem.c_selectedOptionsUI);
  const coverVariant = getSelectedVariantProduct(masterProduct, primaryItem.productId);
  const isMasterPrimary =
    primaryItem.productId === masterProduct.id ||
    primaryItem.productId === masterProduct.productId;
  const primaryGa4Item = buildGa4PrimaryCoverBasketLineItem({
    categoryProduct: productsById[primaryItem.productId] || masterProduct,
    currency,
    imageMap: optionImageMap,
    itemName: masterProduct.name || payload?.coverName || primaryItem.productId,
    line: primaryItem,
    masterProduct,
    price: resolveGa4ItemPrice(primaryItem.productId, {
      linePrice: primaryItem.price ?? coverVariant?.salePrice ?? coverVariant?.price ?? null,
      priceMap: optionPriceMap,
      product: coverVariant || (isMasterPrimary ? masterProduct : null),
    }),
  });
  const items = [];

  if (primaryGa4Item) {
    items.push(primaryGa4Item);
  }

  childItems.forEach((childItem) => {
    const childGa4Item = buildGa4ChildBasketLineItem({
      categoryProduct: productsById[childItem.productId] || masterProduct,
      currency,
      imageMap: optionImageMap,
      itemName: optionNameMap?.[childItem.productId] || childItem.productId,
      line: childItem,
      nameMap: optionNameMap,
      productsById,
      price: resolveGa4ItemPrice(childItem.productId, {
        linePrice: childItem.price,
        priceMap: optionPriceMap,
      }),
    });

    if (childGa4Item) {
      items.push(childGa4Item);
    }
  });

  const kingGuardItem = resolveKingGuardGa4Item({
    categoryProduct: masterProduct,
    lineItem: primaryItem,
    masterProduct,
  });

  return kingGuardItem ? [...items, kingGuardItem] : items;
};

/**
 * Customize PDP add_to_cart — prices/total from PDP context; SCAPI fetch for image/name fallback.
 */
export const trackPdpAddToCartFromCustomize = async ({
  api,
  getToken,
  inventoryIds = null,
  masterProduct,
  primaryItem,
  childItems = [],
  currency,
  customer,
  displayTotal,
  optionPriceMap: callerPriceMap = null,
}) => {
  const payload = parseSelectedOptionsUIPayload(primaryItem?.c_selectedOptionsUI);
  const productIds = [
    primaryItem?.productId,
    ...childItems.map((item) => item.productId)
  ].filter(Boolean);
  const hasCallerPrices = Boolean(callerPriceMap && Object.keys(callerPriceMap).length);
  const {priceMap: fetchedPriceMap, imageMap, nameMap, productsById} = await fetchGa4OptionProductMeta({
    api,
    getToken,
    includePrices: !hasCallerPrices,
    inventoryIds,
    productIds,
  });
  const optionPriceMap = hasCallerPrices ? callerPriceMap : fetchedPriceMap;

  const items = buildGa4AddToCartItemsFromCustomize({
    childItems,
    currency,
    masterProduct,
    optionImageMap: imageMap,
    optionNameMap: nameMap,
    optionPriceMap,
    primaryItem,
    productsById,
  });

  const computedTotal = roundGa4Currency(items.reduce(
    (sum, item) => sum + ((Number(item.price) || 0) * (Number(item.quantity) || 1)),
    0
  )) ?? 0;
  const value = resolveGa4CustomizeEventValue(
    displayTotal,
    payload?.configuredTotal,
    computedTotal
  );

  return trackAddToCart({currency, customer, items, value});
};
